<template>
    <PageWrapper
        :loading="isLoading"
        :non-destructive-on-loading="true"
        title="Custom Properties"
        :description="description"
    >
        <template #headerActions>
            <NewButton variant="fill" color="primary" @click="openCustomPropertyModal()">
                <EidIcon :icon-src="faPlus" />
                Add Custom Property
            </NewButton>
        </template>
        <CustomPropertiesManagementTable
            :items="sortedCustomProperties"
            :display-ids="displayIds"
            :is-saving="isSavingConfig"
            :can-delete="canDeleteCustomProperty"
            @update:display-ids="onDisplayIdsUpdate"
            @reorder="onReorder"
            @add="openCustomPropertyModal()"
            @edit="openCustomPropertyModal"
            @delete="openDeleteModal"
        />
        <DeleteCustomPropertyModal
            v-if="deleteModalProperty"
            :property="deleteModalProperty"
            :usage="deleteModalUsage"
            :rp-name="rpName"
            :loading="deleteModalLoading"
            :submitting="deleteModalSubmitting"
            :error="deleteModalErrorMessage"
            @confirm="onDeleteConfirm"
            @close="onDeleteModalClose"
        />
        <CustomPropertyModal
            v-if="showCustomPropertyModal"
            :property="selectedProperty"
            :submitting="customPropertyModalSubmitting"
            :error="customPropertyModalError"
            :usage-counts="selectedEnumUsageCounts"
            :loading-usage="selectedEnumUsageLoading"
            :reorder-saving="reorderSaving"
            @submit="onCustomPropertySubmit"
            @close="onCustomPropertyModalClose"
            @enum-options-reorder="onEnumOptionsReorder"
        />
    </PageWrapper>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { faPlus } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import { getHttpStatus } from '@evidentid/gateway-api-client';
    import {
        CustomProperty,
        CustomPropertyType,
        CustomPropertyUsage,
        EnumOptionUsage,
        UiConfig,
    } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import CustomPropertiesManagementTable
        from '@/modules/dashboard-configuration/components/custom-properties-management/CustomPropertiesManagementTable/CustomPropertiesManagementTable.vue';
    import CustomPropertyModal
        from '@/modules/dashboard-configuration/components/custom-properties-management/CustomPropertyModal/CustomPropertyModal.vue';
    import DeleteCustomPropertyModal
        from '@/modules/dashboard-configuration/components/custom-properties-management/DeleteCustomPropertyModal/DeleteCustomPropertyModal.vue';
    import { useDashboardConfigurationStore } from '@/modules/dashboard-configuration/store';
    import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';

    const DESCRIPTION = 'Use custom properties to track the information your organization needs for each entity. These properties can be used for filtering, reporting, and assignments. Select up to 3 properties to display in the entity table.';

    export default defineComponent({
        name: 'CustomPropertiesManagementView',
        components: {
            PageWrapper,
            NewButton,
            EidIcon,
            CustomPropertiesManagementTable,
            CustomPropertyModal,
            DeleteCustomPropertyModal,
        },
        data() {
            return {
                faPlus,
                description: DESCRIPTION,
                localUiConfig: null as UiConfig | null,
                isSavingConfig: false,
                lastRpName: '' as string,
                showCustomPropertyModal: false,
                customPropertyModalSubmitting: false,
                customPropertyModalError: null as string | null,
                selectedProperty: null as CustomProperty | null,
                reorderSaving: false as boolean,
                deleteModalProperty: null as CustomProperty | null,
                deleteModalSubmitting: false,
                deleteModalError: null as string | null,
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            rpConfigState(): {
                status: OperationStatus;
                customProperties: CustomProperty[];
                customPropertiesOrder: string[];
                entitiesTableCustomProperties: string[];
            } {
                return useDashboardStore().rpConfig[this.rpName] ?? {
                    status: OperationStatus.uninitialized,
                    customProperties: [],
                    customPropertiesOrder: [],
                    entitiesTableCustomProperties: [],
                };
            },
            updateDisplayStatus(): OperationStatus {
                return useDashboardConfigurationStore().updateDisplayStatus[this.rpName]
                    || OperationStatus.uninitialized;
            },
            isLoading(): boolean {
                return this.rpConfigState.status === OperationStatus.loading;
            },
            effectiveUiConfig(): UiConfig {
                return this.localUiConfig ?? {
                    customPropertiesOrder: this.rpConfigState.customPropertiesOrder,
                    entitiesTableCustomProperties: this.rpConfigState.entitiesTableCustomProperties,
                };
            },
            sortedCustomProperties(): CustomProperty[] {
                return getSortedCustomProperties(
                    this.rpConfigState.customProperties,
                    this.effectiveUiConfig.customPropertiesOrder,
                );
            },
            displayIds(): string[] {
                const displayedKeys = new Set(this.effectiveUiConfig.entitiesTableCustomProperties);
                return this.rpConfigState.customProperties
                    .filter((p) => displayedKeys.has(p.key))
                    .map((p) => p.id);
            },
            selectedEnumUsageCounts(): Record<string, EnumOptionUsage> | null {
                if (!this.selectedProperty || this.selectedProperty.type !== CustomPropertyType.enum) {
                    return null;
                }
                return useDashboardConfigurationStore().enumFieldUsageCounts[this.selectedProperty.id] ?? null;
            },
            selectedEnumUsageLoading(): boolean {
                if (!this.selectedProperty || this.selectedProperty.type !== CustomPropertyType.enum) {
                    return false;
                }
                const usageStatus = useDashboardConfigurationStore().enumFieldUsageStatus[this.selectedProperty.id];
                return usageStatus === OperationStatus.loading;
            },
            /**
             * Access is granted by feature, not by individual API permission — `rbac.userRoles`
             * lists the roles the client has defined, not the role this user holds. Managing
             * custom properties is the feature that already gates this page, so anyone who can
             * see it can delete; the server still enforces `config:deleteCustomProperty`.
             */
            canDeleteCustomProperty(): boolean {
                const userFeatures = useUserStore().currentRpRbacFeatures || {};
                return userFeatures[RbacFeature.configurationCustomProperties] ?? false;
            },
            deleteModalUsage(): CustomPropertyUsage | null {
                if (!this.deleteModalProperty) {
                    return null;
                }
                return useDashboardConfigurationStore()
                    .customPropertyUsage[this.deleteModalProperty.id] ?? null;
            },
            /**
             * A failed usage check leaves no dependencies to show and no delete to offer, so the
             * modal needs something to say — otherwise it renders empty. A delete failure takes
             * precedence, since it is the more specific of the two.
             */
            deleteModalErrorMessage(): string | null {
                if (this.deleteModalError) {
                    return this.deleteModalError;
                }
                if (!this.deleteModalProperty) {
                    return null;
                }
                const status = useDashboardConfigurationStore()
                    .customPropertyUsageStatus[this.deleteModalProperty.id];
                return status === OperationStatus.error
                    ? 'Something went wrong. This property cannot be deleted right now.'
                    : null;
            },
            deleteModalLoading(): boolean {
                if (!this.deleteModalProperty) {
                    return false;
                }
                const status = useDashboardConfigurationStore()
                    .customPropertyUsageStatus[this.deleteModalProperty.id];
                return status === OperationStatus.loading || status === OperationStatus.uninitialized
                    || status === undefined;
            },
        },
        watch: {
            '$rp.current': {
                handler(rpName: string, prevRpName: string) {
                    this.lastRpName = rpName;
                    if (rpName && rpName !== prevRpName) {
                        useRbacStore().validateRbac(
                            [ RbacFeature.configurationCustomProperties ],
                        );
                    }
                    this.loadData();
                    if (prevRpName) {
                        useDashboardStore().clearRpConfig({ rpName: prevRpName });
                        useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName: prevRpName });
                    }
                },
            },
            'rpConfigState': {
                immediate: true,
                deep: true,
                handler(state: { customPropertiesOrder: string[], entitiesTableCustomProperties: string[] }) {
                    if (!this.isSavingConfig && !this.reorderSaving) {
                        this.localUiConfig = {
                            customPropertiesOrder: [ ...state.customPropertiesOrder ],
                            entitiesTableCustomProperties: [ ...state.entitiesTableCustomProperties ],
                        };
                    }
                },
            },
            updateDisplayStatus(newStatus: OperationStatus) {
                if (newStatus === OperationStatus.error) {
                    this.localUiConfig = {
                        customPropertiesOrder: [ ...this.rpConfigState.customPropertiesOrder ],
                        entitiesTableCustomProperties: [ ...this.rpConfigState.entitiesTableCustomProperties ],
                    };
                }
                if (newStatus === OperationStatus.success || newStatus === OperationStatus.error) {
                    this.isSavingConfig = false;
                    useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName: this.rpName });
                }
            },
            reorderSaving(saving: boolean) {
                if (!saving && !this.isSavingConfig && this.rpConfigState.status === OperationStatus.success) {
                    this.localUiConfig = {
                        customPropertiesOrder: [ ...this.rpConfigState.customPropertiesOrder ],
                        entitiesTableCustomProperties: [ ...this.rpConfigState.entitiesTableCustomProperties ],
                    };
                }
            },
        },
        mounted() {
            this.lastRpName = this.rpName;
            if (this.rpName) {
                useRbacStore().validateRbac(
                    [ RbacFeature.configurationCustomProperties ],
                );
                this.loadData();
            }
        },
        unmounted() {
            useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName: this.lastRpName });
            if (this.selectedProperty?.type === CustomPropertyType.enum) {
                useDashboardConfigurationStore().clearEnumFieldUsage({ fieldId: this.selectedProperty.id });
            }
            if (this.deleteModalProperty) {
                useDashboardConfigurationStore()
                    .clearCustomPropertyUsage({ fieldId: this.deleteModalProperty.id });
            }
        },
        methods: {
            loadData() {
                if (this.rpName) {
                    useDashboardStore().loadRpConfig({ rpName: this.rpName });
                }
            },
            onReorder(newOrder: CustomProperty[]) {
                const current = this.effectiveUiConfig;
                this.isSavingConfig = true;

                // Build new key order from the dragged sequence
                const newCustomPropertiesOrder = newOrder.map((p: CustomProperty) => p.key);
                // Re-derive entitiesTableCustomProperties in the new order
                const newUiConfig: UiConfig = {
                    customPropertiesOrder: newCustomPropertiesOrder,
                    entitiesTableCustomProperties: newCustomPropertiesOrder
                        .filter((key) => current.entitiesTableCustomProperties.includes(key)),
                };
                this.localUiConfig = newUiConfig;
                useDashboardConfigurationStore().updateDisplayConfig({
                    rpName: this.rpName,
                    uiConfig: newUiConfig,
                });
            },
            onDisplayIdsUpdate(newIds: string[]) {
                const displayedKeys = this.rpConfigState.customProperties
                    .filter((p: CustomProperty) => newIds.includes(p.id))
                    .map((p: CustomProperty) => p.key);
                // Only express which ids are displayed — updateDisplayConfig derives the
                // full customPropertiesOrder itself (restoring/deduping as needed).
                const newUiConfig: UiConfig = {
                    customPropertiesOrder: this.effectiveUiConfig.customPropertiesOrder,
                    entitiesTableCustomProperties: displayedKeys,
                };
                this.localUiConfig = newUiConfig;
                this.isSavingConfig = true;
                useDashboardConfigurationStore().updateDisplayConfig({
                    rpName: this.rpName,
                    uiConfig: newUiConfig,
                });
            },
            openCustomPropertyModal(property: CustomProperty | null = null) {
                this.selectedProperty = property;
                this.customPropertyModalError = null;
                this.showCustomPropertyModal = true;
                if (property?.type === CustomPropertyType.enum) {
                    useDashboardConfigurationStore().loadEnumFieldOptionsUsage({
                        rpName: this.rpName,
                        fieldId: property.id,
                    });
                }
            },
            async openDeleteModal(property: CustomProperty) {
                this.deleteModalProperty = property;
                this.deleteModalError = null;
                await useDashboardConfigurationStore().loadCustomPropertyUsage({
                    rpName: this.rpName,
                    fieldId: property.id,
                });
                // The usage GET can outlive the modal. Its result lands in the store either way,
                // so a response that arrives after the close has to be cleared again or it
                // defeats the clear that onDeleteModalClose already did.
                if (this.deleteModalProperty?.id !== property.id) {
                    useDashboardConfigurationStore()
                        .clearCustomPropertyUsage({ fieldId: property.id });
                }
            },
            async onDeleteConfirm() {
                if (!this.deleteModalProperty) {
                    return;
                }
                const propertyId = this.deleteModalProperty.id;
                this.deleteModalSubmitting = true;
                this.deleteModalError = null;
                try {
                    await useDashboardConfigurationStore().deleteCustomProperty({
                        rpName: this.rpName,
                        propertyId,
                    });
                    this.onDeleteModalClose();
                } catch (error: any) {
                    // A 409 has already refreshed the usage in the store, so the modal re-renders
                    // in its blocked state — the dependency list is the explanation, not an alert.
                    if (getHttpStatus(error) !== 409) {
                        this.deleteModalError = (typeof error?.message === 'string' && error.message)
                            || 'Unable to delete this custom property. Please try again.';
                    }
                } finally {
                    this.deleteModalSubmitting = false;
                }
            },
            onDeleteModalClose() {
                const property = this.deleteModalProperty;
                this.deleteModalProperty = null;
                this.deleteModalError = null;
                this.deleteModalSubmitting = false;
                if (property) {
                    useDashboardConfigurationStore()
                        .clearCustomPropertyUsage({ fieldId: property.id });
                }
            },
            async onCustomPropertySubmit(form: any) {
                this.customPropertyModalSubmitting = true;
                this.customPropertyModalError = null;
                const propertyBeforeSubmit = this.selectedProperty;
                try {
                    if (propertyBeforeSubmit) {
                        await useDashboardConfigurationStore().updateCustomProperty({
                            rpName: this.rpName,
                            propertyId: propertyBeforeSubmit.id,
                            ...form,
                        });
                    } else {
                        await useDashboardConfigurationStore().createCustomProperty({
                            rpName: this.rpName,
                            ...form,
                        });
                    }
                    await useDashboardStore().loadRpConfig({ rpName: this.rpName });
                    this.showCustomPropertyModal = false;
                    this.selectedProperty = null;
                    if (propertyBeforeSubmit?.type === CustomPropertyType.enum) {
                        useDashboardConfigurationStore().clearEnumFieldUsage({
                            fieldId: propertyBeforeSubmit.id,
                        });
                    }
                } catch (error) {
                    const backendMessage = error instanceof Error && error.message ? error.message : null;
                    this.customPropertyModalError = backendMessage || (propertyBeforeSubmit
                        ? 'Unable to update custom property. Please try again.'
                        : 'Unable to create custom property. Please try again.');
                } finally {
                    this.customPropertyModalSubmitting = false;
                }
            },
            onCustomPropertyModalClose() {
                if (!this.customPropertyModalSubmitting) {
                    const propertyBeforeClose = this.selectedProperty;
                    this.showCustomPropertyModal = false;
                    this.selectedProperty = null;
                    if (propertyBeforeClose?.type === CustomPropertyType.enum) {
                        useDashboardConfigurationStore().clearEnumFieldUsage({
                            fieldId: propertyBeforeClose.id,
                        });
                    }
                }
            },
            async onEnumOptionsReorder(payload: {
                newOrder: string[];
                name: string;
                description: string | null;
                required: boolean;
            }) {
                if (!this.selectedProperty) {
                    return;
                }
                this.reorderSaving = true;
                try {
                    await useDashboardConfigurationStore().updateCustomProperty({
                        rpName: this.rpName,
                        propertyId: this.selectedProperty.id,
                        name: payload.name,
                        description: payload.description,
                        required: payload.required,
                        enumOptions: payload.newOrder,
                    });
                } catch {
                    this.customPropertyModalError = 'Unable to save option order. Please try again.';
                } finally {
                    this.reorderSaving = false;
                }
            },
        },
    });
</script>
